import { appwriteConfig, functions } from '@/lib/appwrite/config';

export interface CreateOrderParams {
  orderId: string;
  orderAmount: number;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  productName?: string;
  returnUrl: string;
  notifyUrl: string;
  // New metadata fields
  customerId?: string;
  dealId?: string;
  streamId?: string;
  productId?: string;
  sellerId?: string;
  subscriptionType?: string; // NEW: For subscription payments
}

export interface VerifyPaymentParams {
  orderId: string;
  paymentId?: string;
  signature?: string;
}

export interface CashfreeOrderResponse {
  return_url: any;
  success: boolean;
  payment_session_id?: string;
  order_id?: string;
  order_status?: string;
  error?: string;
}

export interface CashfreeVerifyResponse {
  success: boolean; // FIXED: Changed from isValid to success to match function response
  isValid: boolean;
  orderDetails?: {
    order_id: string;
    order_status: string;
    order_amount: number;
    payment_method?: string;
    payment_id?: string;
    seller_id?: string;
  };
  error?: string;
  paymentId?: string; // Added for better response handling
  data?: any; // ADDED: To handle the data object from function response
}

// Add Cloudflare Worker webhook URL - UPDATE THIS WITH YOUR ACTUAL WORKER URL
const CLOUDFLARE_WORKER_WEBHOOK_URL = import.meta.env.VITE_CASHFREE_WEBHOOK_URL;

export const createCashfreeOrder = async (params: CreateOrderParams): Promise<CashfreeOrderResponse> => {
  try {
    
    // Prepare the request payload
    const requestPayload = {
      orderId: params.orderId,
      orderAmount: params.orderAmount,
      customerName: params.customerName,
      customerEmail: params.customerEmail,
      customerPhone: params.customerPhone,
      productName: params.productName,
      returnUrl: params.returnUrl,
      notifyUrl: params.notifyUrl,
      // Include metadata fields
      customerId: params.customerId,
      dealId: params.dealId,
      streamId: params.streamId,
      productId: params.productId,
      sellerId: params.sellerId,
      subscriptionType: params.subscriptionType // NEW: Include subscription type
    };

    const response = await functions.createExecution(
      appwriteConfig.cashfreeCreateOrder,
      JSON.stringify(requestPayload)
    );
    
    if (response.responseStatusCode !== 200) {
      throw new Error(`Function execution failed with status: ${response.responseStatusCode}`);
    }

    const result = JSON.parse(response.responseBody);
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to create order');
    }

    return result;
  } catch (error) {
    console.error("Error creating Cashfree order:", error);
    throw new Error(error instanceof Error ? error.message : 'Failed to create order');
  }
};

export const verifyCashfreePayment = async (params: VerifyPaymentParams): Promise<CashfreeVerifyResponse | boolean> => {
  try {
    
    const response = await functions.createExecution(
      appwriteConfig.cashfreeVerifyPayment,
      JSON.stringify(params)
    );
    
    if (response.responseStatusCode !== 200) {
      console.error('Payment verification failed with status:', response.responseStatusCode);
      return false;
    }

    const result = JSON.parse(response.responseBody);
    
    // FIXED: Handle the response structure properly
    if (!result.success) {
      return {
        success: false,
        isValid: false,
        error: result.error,
        orderDetails: undefined,
        paymentId: undefined
      };
    }

    // FIXED: Extract data from the correct response structure
    const orderDetails = result.orderDetails || result.data;
    const paymentId = result.paymentId || orderDetails?.payment_details?.cf_payment_id || orderDetails?.payment_id;
    
    // Return the full response object for better handling
    return {
      success: result.success,
      isValid: result.isValid !== undefined ? result.isValid : (orderDetails?.order_status === 'PAID'),
      orderDetails: orderDetails ? {
        order_id: orderDetails.order_id,
        order_status: orderDetails.order_status,
        order_amount: orderDetails.order_amount,
        payment_method: orderDetails.payment_method || orderDetails.payment_details?.payment_method,
        payment_id: paymentId,
        seller_id: orderDetails.seller_id
      } : undefined,
      error: result.error,
      paymentId: paymentId,
      data: result.data // ADDED: Include full data for debugging
    };
  } catch (error) {
    console.error("Error verifying payment:", error);
    return false;
  }
};

export const getCashfreePaymentDetails = async (orderId: string): Promise<CashfreeVerifyResponse> => {
  try {
    const response = await functions.createExecution(
      appwriteConfig.cashfreeVerifyPayment,
      JSON.stringify({ orderId })
    );

    if (response.responseStatusCode !== 200) {
      throw new Error(`Failed to get payment details: ${response.responseStatusCode}`);
    }

    const result = JSON.parse(response.responseBody);
    
    // FIXED: Handle response structure consistently
    if (!result.success) {
      return {
        success: false,
        isValid: false,
        error: result.error,
        orderDetails: undefined,
        paymentId: undefined
      };
    }

    const orderDetails = result.orderDetails || result.data;
    const paymentId = result.paymentId || orderDetails?.payment_details?.cf_payment_id || orderDetails?.payment_id;
    
    // Add type safety for the response
    return {
      success: result.success,
      isValid: result.isValid !== undefined ? result.isValid : (orderDetails?.order_status === 'PAID'),
      orderDetails: orderDetails ? {
        order_id: orderDetails.order_id,
        order_status: orderDetails.order_status,
        order_amount: orderDetails.order_amount,
        payment_method: orderDetails.payment_method || orderDetails.payment_details?.payment_method,
        payment_id: paymentId,
        seller_id: orderDetails.seller_id
      } : undefined,
      error: result.error,
      paymentId: paymentId,
      data: result.data
    };
  } catch (error) {
    console.error("Error getting payment details:", error);
    throw error;
  }
};

// Existing function to handle deal payments
export const createDealPaymentOrder = async (
  dealId: string,
  amount: number,
  customer: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
  },
  sellerId: string,
  returnUrl: string
): Promise<CashfreeOrderResponse> => {
  return createCashfreeOrder({
    orderId: `deal_${dealId}_${Date.now()}`,
    orderAmount: amount,
    customerName: customer.name,
    customerEmail: customer.email || '',
    customerPhone: customer.phone || '',
    returnUrl: returnUrl,
    // FIXED: Use Cloudflare Worker webhook URL instead of direct Appwrite
    notifyUrl: `${CLOUDFLARE_WORKER_WEBHOOK_URL}?type=deal&dealId=${dealId}`,
    customerId: customer.id,
    dealId: dealId,
    sellerId: sellerId
  });
};

// Existing function to handle stream purchases
export const createStreamPurchaseOrder = async (
  streamId: string,
  productId: string,
  amount: number,
  customer: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
  },
  sellerId: string,
  productName: string,
  returnUrl: string
): Promise<CashfreeOrderResponse> => {
  return createCashfreeOrder({
    orderId: `stream_${streamId}_${Date.now()}`,
    orderAmount: amount,
    customerName: customer.name,
    customerEmail: customer.email || '',
    customerPhone: customer.phone || '',
    productName: productName,
    returnUrl: returnUrl,
    // FIXED: Use Cloudflare Worker webhook URL instead of direct Appwrite
    notifyUrl: `${CLOUDFLARE_WORKER_WEBHOOK_URL}?type=stream&streamId=${streamId}&productId=${productId}`,
    customerId: customer.id,
    streamId: streamId,
    productId: productId,
    sellerId: sellerId
  });
};

// NEW: Function to handle subscription payments
export const createSubscriptionPaymentOrder = async (
  userId: string,
  amount: number,
  points: number,
  customer: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
  },
  subscriptionType: string, // e.g., 'trust_points', 'premium_subscription'
  returnUrl: string
): Promise<CashfreeOrderResponse> => {
  return createCashfreeOrder({
    orderId: `subscription_${userId}_${Date.now()}`,
    orderAmount: amount,
    customerName: customer.name,
    customerEmail: customer.email || '',
    customerPhone: customer.phone || '',
    productName: `${subscriptionType} - ${points} points`,
    returnUrl: returnUrl,
    // Use Cloudflare Worker webhook URL for subscription payments
    notifyUrl: `${CLOUDFLARE_WORKER_WEBHOOK_URL}?type=subscription&userId=${userId}&subscriptionType=${subscriptionType}&points=${points}`,
    customerId: customer.id,
    subscriptionType: subscriptionType
  });
};